import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { getLoginUrl } from '@/utils/domainUtils';
import { auditLogService } from '@/services/auditLogService';
import { setUserId, setUserProperties, getAdoptedFeatures, trackAuth, trackEngagement } from '@/utils/analytics';
import { startSession, endSession } from '@/utils/sessionTracker';
import {
  createSession,
  endSession as endGLBASession,
  startHeartbeatInterval,
  stopHeartbeat,
  GLBA_SESSION_ID_KEY,
  GLBA_ORG_ID_KEY,
} from '@/services/sessionService';

export interface User {
  id: string;
  name: string;
  email: string;
  picture?: string;
  verified_email: boolean;
  organizationId?: string;
  role?: string;
  preferredLanguage?: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: User, organizationId?: string) => Promise<void>;
  logout: () => void;
  checkSession: () => boolean;
  updateUser: (updates: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  useEffect(() => {
    // Check if user is logged in from localStorage
    const savedUser = localStorage.getItem('user');
    const lastLogin = localStorage.getItem('lastLogin');
    
    if (savedUser && lastLogin) {
      try {
        const lastLoginTime = parseInt(lastLogin, 10);
        const now = Date.now();
        const timeElapsed = now - lastLoginTime;
        
        // Check if session has expired
        if (timeElapsed > SESSION_TIMEOUT) {
          console.log('⚠️ Session expired, logging out user');
          // End GLBA session (Enterprise) before clearing
          const glbaSessionId = localStorage.getItem(GLBA_SESSION_ID_KEY);
          const glbaOrgId = localStorage.getItem(GLBA_ORG_ID_KEY);
          if (glbaSessionId && glbaOrgId) {
            endGLBASession(glbaSessionId, glbaOrgId, 'timeout').catch(() => {});
          }
          localStorage.removeItem('user');
          localStorage.removeItem('lastLogin');
          localStorage.removeItem(GLBA_SESSION_ID_KEY);
          localStorage.removeItem(GLBA_ORG_ID_KEY);
          localStorage.removeItem('pendingAuditLog'); // Clear pending audit log
          setUser(null);
        } else {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          
          // Process pending audit log from previous login (if any)
          const pendingAuditLog = localStorage.getItem('pendingAuditLog');
          if (pendingAuditLog) {
            try {
              const auditData = JSON.parse(pendingAuditLog);
              // Only process if it's recent (within last 5 minutes) to avoid processing old logs
              const logAge = Date.now() - (auditData.timestamp || 0);
              if (logAge < 5 * 60 * 1000) { // 5 minutes
                console.log('📝 Processing pending audit log from login...');
                (async () => {
                  try {
                    let finalOrgId = auditData.organizationId;
                    
                    // Fetch organizationId if not available
                    if (!finalOrgId) {
                      const { doc, getDoc } = await import('firebase/firestore');
                      const { db } = await import('@/firebase/config');
                      const userDoc = await getDoc(doc(db, 'users', auditData.userId));
                      if (userDoc.exists()) {
                        const firestoreUserData = userDoc.data();
                        finalOrgId = firestoreUserData.organizationId;
                      }
                    }
                    
                    if (finalOrgId) {
                      const result = await auditLogService.log({
                        userId: auditData.userId,
                        action: auditData.action,
                        performedBy: auditData.performedBy,
                        performedByName: auditData.performedByName,
                        performedByEmail: auditData.performedByEmail,
                        organizationId: finalOrgId,
                      });
                      
                      if (result.success) {
                        console.log('✅ Pending audit log processed successfully, log ID:', result.id);
                        localStorage.removeItem('pendingAuditLog');
                      } else {
                        console.error('❌ Failed to process pending audit log:', result.error);
                        // Keep the flag so we can retry later
                      }
                    } else {
                      console.warn('⚠️ No organizationId available for pending audit log');
                      localStorage.removeItem('pendingAuditLog');
                    }
                  } catch (error: any) {
                    console.error('❌ Error processing pending audit log:', error);
                    // Keep the flag so we can retry later
                  }
                })();
              } else {
                console.log('⚠️ Pending audit log is too old, discarding');
                localStorage.removeItem('pendingAuditLog');
              }
            } catch (error) {
              console.error('❌ Error parsing pending audit log:', error);
              localStorage.removeItem('pendingAuditLog');
            }
          }
          
          // Sync email verification status from Firebase Auth
          // NOTE: For invited users, we trust the Firestore verified_email flag (already verified via invitation)
          // Only sync from Firebase Auth for non-invited users who haven't verified yet
          const syncEmailVerification = async () => {
            const { auth } = await import('@/firebase/config');
            const { reload } = await import('firebase/auth');
            const { doc, getDoc } = await import('firebase/firestore');
            const { db } = await import('@/firebase/config');
            
            if (auth?.currentUser && parsedUser && !parsedUser.verified_email) {
              try {
                // Check Firestore first - invited users have verified_email: true in Firestore
                const userDoc = await getDoc(doc(db, 'users', parsedUser.id));
                if (userDoc.exists()) {
                  const userData = userDoc.data();
                  // If Firestore has verified_email flag, use it (for invited users)
                  if (userData.verified_email === true) {
                    const updatedUser = { ...parsedUser, verified_email: true };
                    setUser(updatedUser);
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    console.log('✅ Email verified via invitation (from Firestore)');
                    return;
                  }
                }
                
                // For non-invited users, check Firebase Auth status
                await reload(auth.currentUser);
                const isVerified = auth.currentUser.emailVerified;
                
                // If verified status changed, update user
                if (isVerified !== parsedUser.verified_email) {
                  const updatedUser = { ...parsedUser, verified_email: isVerified };
                  setUser(updatedUser);
                  localStorage.setItem('user', JSON.stringify(updatedUser));
                  console.log('✅ Email verification status synced from Firebase Auth:', isVerified);
                }
              } catch (error) {
                console.error('Error syncing email verification:', error);
              }
            }
          };
          
          syncEmailVerification();
          
          console.log(`✅ Session valid. Time remaining: ${Math.floor((SESSION_TIMEOUT - timeElapsed) / (60 * 60 * 1000))} hours`);
          
          // Initialize FCM for push notifications if user is logged in (non-blocking)
          (async () => {
            try {
              const { initializeFCM } = await import('@/services/fcmService');
              await initializeFCM();
            } catch (error) {
              console.warn('Failed to initialize FCM:', error);
            }
          })();
        }
      } catch (error) {
        console.error('Error parsing saved user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('lastLogin');
      }
    }
    setIsLoading(false);
  }, []);

  // Check session validity periodically and refresh Firebase token
  useEffect(() => {
    let lastTokenRefresh = 0;
    const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes
    
    const checkSessionInterval = setInterval(async () => {
      const lastLogin = localStorage.getItem('lastLogin');
      if (lastLogin) {
        const lastLoginTime = parseInt(lastLogin, 10);
        const now = Date.now();
        const timeElapsed = now - lastLoginTime;
        
        if (timeElapsed > SESSION_TIMEOUT) {
          console.log('⚠️ Session timeout detected, auto-logging out');
          handleSessionTimeout();
        } else {
          // Proactively refresh Firebase Auth token every 30 minutes to prevent expiration
          // Firebase tokens typically expire after 1 hour, so refreshing every 30 min keeps them fresh
          const timeSinceLastRefresh = now - lastTokenRefresh;
          if (timeSinceLastRefresh >= TOKEN_REFRESH_INTERVAL) {
            try {
              const { auth } = await import('@/firebase/config');
              const { reload, getIdToken } = await import('firebase/auth');
              if (auth?.currentUser) {
                console.log('🔄 Proactively refreshing Firebase Auth token...');
                await reload(auth.currentUser);
                await getIdToken(auth.currentUser, true); // Force refresh
                lastTokenRefresh = now;
                console.log('✅ Firebase Auth token refreshed proactively');
              }
            } catch (error) {
              console.warn('⚠️ Failed to proactively refresh token:', error);
              // Don't logout on proactive refresh failure, only on actual errors
            }
          }
        }
      }
    }, 60000); // Check every minute

    return () => clearInterval(checkSessionInterval);
  }, []);

  const handleSessionTimeout = () => {
    stopHeartbeat();
    const glbaSessionId = localStorage.getItem(GLBA_SESSION_ID_KEY);
    const glbaOrgId = localStorage.getItem(GLBA_ORG_ID_KEY);
    if (glbaSessionId && glbaOrgId) {
      endGLBASession(glbaSessionId, glbaOrgId, 'timeout').catch(() => {});
    }
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('lastLogin');
    localStorage.removeItem(GLBA_SESSION_ID_KEY);
    localStorage.removeItem(GLBA_ORG_ID_KEY);
    
    // Disable auto-select for Google
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
    
    // Redirect to login page using current base URL
    window.location.href = getLoginUrl();
  };

  const login = async (userData: User, organizationId?: string) => {
    const orgId = organizationId || userData.organizationId;
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem('lastLogin', Date.now().toString());
    console.log('✅ User logged in, session started');
    
    // Set analytics user properties
    setUserId(userData.id);
    
    // Get adopted features count for user properties
    const adoptedFeatures = getAdoptedFeatures();
    
    if (orgId) {
      setUserProperties({
        organization_id: orgId,
        features_adopted_count: adoptedFeatures.length,
        ...(adoptedFeatures.length > 0 && { 
          last_feature_adopted: adoptedFeatures[adoptedFeatures.length - 1] 
        }),
      });
    }
    
    // Start session tracking
    startSession();
    
    // GLBA session management: Option A (Enterprise) or Option B (non-Enterprise)
    if (orgId) {
      try {
        const sessionResult = await createSession(orgId);
        if (sessionResult.success && sessionResult.mode === 'server_session' && sessionResult.sessionId) {
          localStorage.setItem(GLBA_SESSION_ID_KEY, sessionResult.sessionId);
          localStorage.setItem(GLBA_ORG_ID_KEY, orgId);
          startHeartbeatInterval(sessionResult.sessionId, orgId);
        } else {
          localStorage.removeItem(GLBA_SESSION_ID_KEY);
          localStorage.removeItem(GLBA_ORG_ID_KEY);
        }
      } catch (error) {
        console.warn('GLBA session create failed (non-blocking):', error);
      }
    }
    
    // Initialize FCM for push notifications (non-blocking)
    (async () => {
      try {
        const { initializeFCM } = await import('@/services/fcmService');
        await initializeFCM();
      } catch (error) {
        console.warn('Failed to initialize FCM:', error);
      }
    })();
    
    // Store flag to log audit entry on next page load (better UX - doesn't block redirect)
    if (orgId) {
      localStorage.setItem('pendingAuditLog', JSON.stringify({
        userId: userData.id,
        action: 'user_login',
        performedBy: userData.id,
        performedByName: userData.name || 'Unknown',
        performedByEmail: userData.email || 'Unknown',
        organizationId: orgId,
        timestamp: Date.now(),
      }));
      console.log('📝 Audit log queued for next page load');
    } else {
      // If organizationId not provided, store a flag to fetch it on next load
      localStorage.setItem('pendingAuditLog', JSON.stringify({
        userId: userData.id,
        action: 'user_login',
        performedBy: userData.id,
        performedByName: userData.name || 'Unknown',
        performedByEmail: userData.email || 'Unknown',
        organizationId: null, // Will be fetched on next load
        timestamp: Date.now(),
      }));
      console.log('📝 Audit log queued (will fetch organizationId on next page load)');
    }
  };

  const logout = () => {
    // Track engagement before logout
    try {
      trackEngagement();
    } catch (error) {
      console.warn('Failed to track engagement on logout:', error);
    }
    
    // Stop GLBA heartbeat and end session (Enterprise Option A or Option B logout event)
    stopHeartbeat();
    const glbaSessionId = localStorage.getItem(GLBA_SESSION_ID_KEY);
    const glbaOrgId = localStorage.getItem(GLBA_ORG_ID_KEY);
    if (glbaOrgId) {
      endGLBASession(glbaSessionId, glbaOrgId, 'logout').catch(() => {});
    }
    localStorage.removeItem(GLBA_SESSION_ID_KEY);
    localStorage.removeItem(GLBA_ORG_ID_KEY);
    
    // End session tracking
    try {
      endSession();
    } catch (error) {
      console.warn('Failed to end session on logout:', error);
    }
    
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('lastLogin');
    
    // Track logout
    trackAuth.logout();
    
    // Disable auto-select for Google
    if (window.google && window.google.accounts && window.google.accounts.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  const checkSession = () => {
    const lastLogin = localStorage.getItem('lastLogin');
    if (!lastLogin) return false;
    
    const lastLoginTime = parseInt(lastLogin, 10);
    const now = Date.now();
    const timeElapsed = now - lastLoginTime;
    
    return timeElapsed < SESSION_TIMEOUT;
  };

  const updateUser = useCallback((updates: Partial<User>) => {
    if (!user) return;
    
    // Only update if something actually changed
    const hasChanges = Object.keys(updates).some(key => {
      return user[key as keyof User] !== updates[key as keyof User];
    });
    
    if (!hasChanges) return;
    
    const updatedUser = { ...user, ...updates };
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  }, [user]);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkSession,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
